import { assetsClient } from '@/clients/assets-client.js';
import type { Image } from '@/clients/assets-client.js';
import { backboneService } from '@/services/backbone.js';
import { HatElement } from '@/components/hat-element.js';

class HatLocationForm extends HatElement {
  private _locationId: string | null = null;
  private _data: any = null;

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  static get observedAttributes() {
    return ['location-id'];
  }

  attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null) {
    if (name === 'location-id' && newVal !== oldVal) {
      this._locationId = newVal;
      this.loadData();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._renderShell();

    if (this.hasAttribute('location-id')) {
      this._locationId = this.getAttribute('location-id');
      this.loadData();
    }
  }

  // ─── Data ─────────────────────────────────────────────────────────────────

  async loadData() {
    if (!this._locationId) return;

    this._data = await assetsClient.getLocation(this._locationId);

    this._populateForm();

    const gallery = this.root.querySelector('hat-location-gallery')!;
    gallery.setImages(this._data.images || []);

    if (this._data.displayImage?.id) {
      gallery.setAttribute('primary-image-id', this._data.displayImage.id);
    }
  }

  _populateForm() {
    if (!this._data) return;

    const form = this.root.querySelector('form') as any;
    form.name.value        = this._data.name                  || '';
    form.street1.value     = this._data.address?.street1      || '';
    form.street2.value     = this._data.address?.street2      || '';
    form.city.value        = this._data.address?.city         || '';
    form.state.value       = this._data.address?.state        || '';
    form.postalCode.value  = this._data.address?.postalCode   || '';
    form.country.value     = this._data.address?.country      || '';
  }

  // ─── Spinner ──────────────────────────────────────────────────────────────

  _showSpinner() {
    const spinner = document.createElement('div');
    spinner.className = 'spinner-overlay';
    spinner.innerHTML = `
      <div class="spinner-box">
        <div class="spinner"></div>
        <div class="spinner-label">Saving…</div>
      </div>
    `;
    this.root.appendChild(spinner);
  }

  _hideSpinner() {
    this.root.querySelector('.spinner-overlay')?.remove();
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  _renderShell() {
    this.root.innerHTML = `
      <style>
        .container {
          max-width: 900px;
          margin: 20px auto;
        }

        .card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        input {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          flex: 1;
          box-sizing: border-box;
          font-size: 14px;
        }

        .field {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .field label {
          min-width: 90px;
          font-size: 13px;
          color: #555;
          text-align: right;
          flex-shrink: 0;
        }

        .name-address-divider {
          text-align: center;
          font-weight: 500;
          color: #666;
          margin: 4px 0;
        }

        .actions {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
        }

        .capture-tabs{
            display:flex;
            gap:8px;
        }

        .mode-btn{
            flex:1;
            border:none;
            padding:10px;
            border-radius:10px;
            cursor:pointer;
            background:#eee;
        }

        .mode-btn.active{
            background:#111;
            color:white;
        }

        .save {
          background: #111;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 10px;
          cursor: pointer;
        }

        .save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .validation-error {
          display: none;
          color: #c0392b;
          font-size: 13px;
          padding: 8px 12px;
          background: #fdf0ef;
          border-radius: 8px;
          border: 1px solid #f5c6c2;
        }

        .validation-error.visible {
          display: block;
        }

        @media (max-width: 600px) {
          .grid { grid-template-columns: 1fr; }
        }

        .modal {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          box-sizing: border-box;
        }

        .modal img,
        .modal hat-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 12px;
        }

        .spinner-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .spinner-box {
          background: white;
          border-radius: 16px;
          padding: 32px 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2);
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #eee;
          border-top-color: #111;
          border-radius: 50%;
          animation: spin 0.75s linear infinite;
        }

        .spinner-label {
          font-size: 15px;
          font-weight: 500;
          color: #444;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>

      <div class="container">
        <div class="card">
          <form>
            <div class="field">
              <label>Name</label>
              <input name="name" autocomplete="off" />
            </div>

            <div class="name-address-divider">- And / Or -</div>

            <div class="field">
              <label>Street 1</label>
              <input name="street1" />
            </div>
            <div class="field">
              <label>Street 2</label>
              <input name="street2" />
            </div>
            <div class="field">
              <label>City</label>
              <input name="city" />
            </div>
            <div class="field">
              <label>State</label>
              <input name="state" />
            </div>
            <div class="field">
              <label>Postal Code</label>
              <input name="postalCode" />
            </div>
            <div class="field">
              <label>Country</label>
              <input name="country" />
            </div>

            <div class="capture-tabs">

              <button
                  class="mode-btn active"
                  type="button"
                  data-mode="camera">
                  Camera
              </button>

              <button
                  class="mode-btn"
                  type="button"
                  data-mode="upload">
                  Upload Files
              </button>

          </div>

          <div class="capture-container">

              <hat-camera-capture></hat-camera-capture>

              <hat-file-upload
                  style="display:none">
              </hat-file-upload>

          </div>

            <hat-location-gallery mode="edit"></hat-location-gallery>

            <div class="validation-error">Please enter a name or at least one address field.</div>

            <div class="actions">
              <button class="save" type="submit">Save</button>
              <hat-nav-button href="/app">Cancel</hat-nav-button>
            </div>
          </form>
        </div>
      </div>
    `;

    this._bindEvents();
  }

  _bindEvents() {
    const form     = this.root.querySelector<HTMLFormElement>('form')!;
    const camera   = this.root.querySelector<HTMLElement>('hat-camera-capture')!;
    const uploader = this.root.querySelector<HTMLElement>('hat-file-upload')!;
    const gallery  = this.root.querySelector('hat-location-gallery')!;
    const saveBtn  = this.root.querySelector<HTMLButtonElement>('.save')!;

    // Forward captured photos into the gallery
    [camera, uploader].forEach(
        source => {
            source.addEventListener('photo-captured',(e) => {
              gallery.addPhoto((e as CustomEvent).detail);
            });
        }
    );

    // Expand modal
    gallery.addEventListener('image-expanded', (e: Event) => {
      this._openImage((e as CustomEvent).detail);
    });

    const validationError = this.root.querySelector<HTMLElement>('.validation-error')!;

    form.addEventListener('input', () => validationError.classList.remove('visible'));

    form.onsubmit = async (e) => {
      e.preventDefault();

      const f = form as any;
      const hasValue = (s: string) => s.trim().length > 0;
      const isValid = hasValue(f.name.value) || hasValue(f.street1.value) ||
                      hasValue(f.street2.value) || hasValue(f.city.value) ||
                      hasValue(f.state.value) || hasValue(f.postalCode.value) ||
                      hasValue(f.country.value);

      if (!isValid) {
        validationError.classList.add('visible');
        return;
      }

      saveBtn.disabled = true;
      this._showSpinner();

      try {
        if (!this._locationId) {
          await this._create(form, gallery);
        } else {
          await this._update(form, gallery);
        }

        this.dispatchEvent(new CustomEvent('save', { bubbles: true }));
        backboneService.navigateTo(`/app/locations/${this._locationId}?is-new=true`);

      } catch (err) {
        console.error('Save failed', err);
        this._hideSpinner();
        saveBtn.disabled = false;
      }
    };

    const buttons = this.root.querySelectorAll<HTMLElement>('.mode-btn');

    buttons.forEach(btn => {

        btn.addEventListener(
            'click',
            () => {

                const mode = btn.dataset.mode;
                buttons.forEach(
                    b => b.classList.remove('active')
                );
                btn.classList.add('active');
                camera.style.display = mode === 'camera' ? '' : 'none';
                uploader.style.display = mode === 'upload' ? '' : 'none';
            }
        );
    });
  }

  // ─── Save logic ───────────────────────────────────────────────────────────

  async _create(form: HTMLFormElement, gallery: any) {
    const fd = new FormData(form);

    gallery.newPhotos.forEach((p: any) => fd.append('files', p.blob));

    let displayIndex = 0;
    const primaryId = gallery.primaryImageId;

    if (primaryId) {
      const idx = gallery.newPhotos.findIndex((p: any) => p.tempId === primaryId);
      if (idx >= 0) displayIndex = idx;
    }

    fd.append('displayImageIndex', displayIndex.toString());

    const response = await assetsClient.createLocation(fd);
    this._locationId = response.id ?? null;
  }

  async _update(form: HTMLFormElement, gallery: any) {
    // Delete removed images
    for (const imageId of gallery.deletedImageIds) {
      await assetsClient.deleteLocationImage(this._locationId!, imageId);
    }

    // Upload new photos
    let uploadedImages: Image[] = [];

    if (gallery.newPhotos.length > 0) {
      const upload = new FormData();
      gallery.newPhotos.forEach((p: any) => upload.append('files', p.blob));
      uploadedImages = await assetsClient.uploadLocationImages(this._locationId!, upload);
    }

    // Resolve primary image id — may be a tempId that now has a real server id
    let resolvedPrimaryId = gallery.primaryImageId;
    const newPhotoIndex = gallery.newPhotos.findIndex(
      (p: any) => p.tempId === gallery.primaryImageId
    );
    if (newPhotoIndex >= 0 && uploadedImages[newPhotoIndex]) {
      resolvedPrimaryId = uploadedImages[newPhotoIndex].id;
    }

    const f = form as any;
    const payload = {
      name: f.name.value,
      address: {
        street1:    f.street1.value,
        street2:    f.street2.value,
        city:       f.city.value,
        state:      f.state.value,
        postalCode: f.postalCode.value,
        country:    f.country.value
      },
      displayImageId: resolvedPrimaryId
    };

    const response = await assetsClient.updateLocation(this._locationId!, payload);
    this._locationId = response.id ?? null;
  }

  // ─── Modal ────────────────────────────────────────────────────────────────

  _openImage({ url, documentId }: { url?: string; documentId?: string }) {
    const modal = document.createElement('div');
    modal.className = 'modal';

    let content;

    if (documentId) {
      content = document.createElement('hat-image');
      content.setAttribute('type', 'expanded');
      content.setAttribute('document-id', documentId);
      content.style.cssText = 'width: auto; height: auto; max-width: 90vw; max-height: 90vh;';
    } else {
      content = document.createElement('img');
      content.src = url ?? '';
    }

    modal.appendChild(content);
    modal.onclick = () => modal.remove();
    this.root.appendChild(modal);
  }
}

customElements.define('hat-location-form', HatLocationForm);